import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react"
import { api } from "../api/http"
import {
	clearSession,
	hashPassword,
	parseSession,
	restoreSession,
	setToken,
	type Role,
	type Session,
} from "./session"

interface AuthValue {
	session: Session | null
	login: (username: string, password: string) => Promise<void>
	logout: () => void
	hasRole: (...roles: Role[]) => boolean
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
	const [session, setSession] = useState<Session | null>(() => restoreSession())

	const login = useCallback(async (username: string, password: string) => {
		const hash = await hashPassword(password)
		const token = await api.get<string>(
			"/api/auth",
			{ username, password: hash },
			false
		)
		const next = parseSession(String(token).trim())
		if (!next) throw new Error("Сервер вернул токен, который не удалось разобрать")
		setToken(next.token)
		setSession(next)
	}, [])

	const logout = useCallback(() => {
		clearSession()
		setSession(null)
	}, [])

	const value = useMemo<AuthValue>(
		() => ({
			session,
			login,
			logout,
			hasRole: (...roles: Role[]) =>
				!!session && roles.some(role => session.roles.includes(role)),
		}),
		[session, login, logout]
	)

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
	const value = useContext(AuthContext)
	if (!value) throw new Error("useAuth вызван вне AuthProvider")
	return value
}
